import React from "react";
import { CHART_COLORS } from "@/features/investors/components/charts/palette";
import { Sparkline } from "@/features/investors/components/charts/Sparkline";
import {
    DeltaChip,
    KpiCapsule,
    MetricText,
    SectionHead,
} from "@/features/investors/components/atoms/InvestorsAtoms";
import { InvestorsInsights } from "@/features/investors/utils/investors-insights";
import { InvestorsDashboardData } from "@/features/investors/types/investors.types";
import { investorsFormatters as fmt } from "@/features/investors/utils/investors-formatters";

type Props = {
    data: InvestorsDashboardData;
    insights: InvestorsInsights;
};

export const ExecutiveSummarySection: React.FC<Props> = ({ data, insights }) => {
    const { executive, growth } = data;
    const monthly = growth.monthly;
    const spark = (select: (month: (typeof monthly)[number]) => number): number[] =>
        monthly.map(select);

    return (
        <section className="sec" id="sec-summary">
            <SectionHead
                index="01"
                title="Executive summary"
                question="The eight numbers that define the quarter, and what they add up to."
            />
            <div
                className="grid"
                style={{ gridTemplateColumns: "2.7fr 1fr", alignItems: "stretch" }}
            >
                <div className="grid" style={{ gridTemplateColumns: "repeat(4,1fr)", gap: "var(--kpi-gap)" }}>
                    <KpiCapsule
                        label="GMV / Volume"
                        value={<MetricText metric={executive.gmv} render={fmt.moneyCompact} />}
                        spark={<Sparkline values={spark((month) => month.gmv)} color={CHART_COLORS.blue} />}
                        delta={<DeltaChip metric={executive.gmv} />}
                        reason={executive.gmv.reason}
                    />
                    <KpiCapsule
                        label="Revenue"
                        value={<MetricText metric={executive.revenue} render={fmt.moneyCompact} />}
                        spark={<Sparkline values={spark((month) => month.revenue)} color={CHART_COLORS.teal} />}
                        delta={<DeltaChip metric={executive.revenue} />}
                    />
                    <KpiCapsule
                        label="Gross profit"
                        value={<MetricText metric={executive.profit} render={fmt.moneyCompact} />}
                        spark={<Sparkline values={spark((month) => month.profit)} color={CHART_COLORS.green} />}
                        delta={<DeltaChip metric={executive.profit} />}
                    />
                    <KpiCapsule
                        label="Gross margin"
                        value={<MetricText metric={executive.grossMarginPct} render={(value) => fmt.pct(value)} />}
                        spark={
                            <Sparkline
                                values={monthly.map((month) =>
                                    month.revenue === 0 ? 0 : (month.profit / month.revenue) * 100
                                )}
                                color={CHART_COLORS.lime}
                            />
                        }
                        delta={<DeltaChip metric={executive.grossMarginPct} format="pp" />}
                    />
                    <KpiCapsule
                        label="Active accounts"
                        value={<MetricText metric={executive.activeAccounts} render={fmt.num} />}
                        spark={
                            <Sparkline values={spark((month) => month.activeAccounts)} color={CHART_COLORS.blue} />
                        }
                        delta={<DeltaChip metric={executive.activeAccounts} />}
                        reason={executive.activeAccounts.reason}
                    />
                    <KpiCapsule
                        label="Completed operations"
                        value={<MetricText metric={executive.completedOperations} render={fmt.numShort} />}
                        spark={
                            <Sparkline values={spark((month) => month.operations)} color={CHART_COLORS.violet} />
                        }
                        delta={<DeltaChip metric={executive.completedOperations} />}
                    />
                    <KpiCapsule
                        label="Take rate"
                        value={
                            <MetricText metric={executive.takeRatePct} render={(value) => value.toFixed(2) + "%"} />
                        }
                        spark={
                            <Sparkline
                                values={monthly.map((month) =>
                                    month.gmv === 0 ? 0 : (month.revenue / month.gmv) * 100
                                )}
                                color={CHART_COLORS.teal}
                            />
                        }
                        delta={<DeltaChip metric={executive.takeRatePct} format="bps" />}
                    />
                    <KpiCapsule
                        label="Repeat usage"
                        value={
                            <MetricText metric={executive.repeatUsageRatePct} render={(value) => fmt.pct(value, 0)} />
                        }
                        spark={
                            <Sparkline values={spark((month) => month.activeUsers)} color={CHART_COLORS.pink} />
                        }
                        delta={<DeltaChip metric={executive.repeatUsageRatePct} format="pp" />}
                    />
                </div>
                <div className="ai">
                    <span className="badge">
                        <span className="mso material-symbols-outlined">auto_awesome</span>Summary
                    </span>
                    <h3>{insights.summary.headline}</h3>
                    <p dangerouslySetInnerHTML={{ __html: insights.summary.body }} />
                    <div className="chips">
                        {insights.summary.chips.map((chip) => (
                            <span key={chip.label} className="chip">
                                <span className="mso material-symbols-outlined">{chip.icon}</span>
                                {chip.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
